/**
 * AssignmentConfigSection — Unified distribution configuration UI (Phase 1b)
 *
 * Componente único usado por canal, funil e pipeline de ticket. Consome
 * `DistributionConfig` direto de `@troia-v3/types` — zero conversões
 * entre shapes locais/remotos.
 *
 * Layout:
 *  1. Toggle Ativar / Desativar distribuição
 *  2. Só em canais: "Priorizar o responsável pelo lead" (2026-10-01 —
 *     `preferLeadOwner`, ausente = ligada; ler por `isLeadOwnerPreferred`)
 *  3. Seletor de estratégia (6 opções)
 *  4. Campos condicionais por estratégia
 *
 * Ver plano: DOCS/architecture/ASSIGNMENTS_REMOVAL.md §4.13
 */

import React from 'react';
import { Icon } from '@/shared/icons';
import { Select, Switch } from '@/shared/components/ui';
import type { DistributionConfig, DistributionStrategy } from '@troia-v3/types';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import {
  AssignmentConfigSectionProps,
  STRATEGY_OPTIONS,
} from './types';

export const AssignmentConfigSection: React.FC<AssignmentConfigSectionProps> = ({
  config,
  onChange,
  resourceType,
  variant = 'card',
}) => {
  const updateConfig = (updates: Partial<DistributionConfig>) => {
    onChange({ ...config, ...updates });
  };

  const { data: usersData = [] } = useUsersDropdown({ status: 'active' });
  const { data: teamsData = [] } = useTeamsDropdown();

  const handleStrategyChange = (type: DistributionStrategy) => {
    // Reset per-strategy sub-configs when switching type so the payload
    // stays clean — prevents stale `fixedOperatorConfig` tagging along
    // when the admin picks another strategy.
    const next: DistributionConfig = {
      enabled: config.enabled,
      type,
      // A opção do responsável pelo lead não depende da estratégia — trocar de
      // estratégia não pode desfazer a escolha de quem desligou.
      ...(config.preferLeadOwner !== undefined ? { preferLeadOwner: config.preferLeadOwner } : {}),
    };

    if (type === 'fixed_operator') {
      next.fixedOperatorConfig = config.fixedOperatorConfig ?? { userId: '' };
    } else if (type === 'fixed_team') {
      next.fixedTeamConfig = config.fixedTeamConfig ?? { teamId: '' };
    }

    onChange(next);
  };

  const wrapperStyles = variant === 'card' ? {
    backgroundColor: 'var(--theme-card-background)',
    borderColor: 'var(--theme-card-border)',
  } : {};

  const wrapperClassName = variant === 'card' ? 'rounded-lg border p-6' : '';

  const contextNoun = (() => {
    switch (resourceType) {
      case 'channel': return 'conversas';
      case 'funnel': return 'leads';
      case 'pipeline': return 'tickets';
    }
  })();

  return (
    <div className={wrapperClassName} style={wrapperStyles}>
      {variant === 'card' && (
        <div className="flex items-center gap-3 mb-6">
          <div
            className="p-2 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-primary-background)',
              color: 'var(--theme-primary)',
            }}
          >
            <Icon name="settings" className="w-5 h-5" />
          </div>
          <div>
            <h2
              className="text-lg font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Configuração de Distribuição
            </h2>
            <p
              className="text-sm"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Define como {contextNoun} serão atribuídas automaticamente
            </p>
          </div>
        </div>
      )}

      {/* Ativar / Desativar toggle */}
      <div className="flex items-center justify-between py-3">
        <div>
          <div
            className="text-sm font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Ativar distribuição automática
          </div>
          <div
            className="text-xs mt-0.5"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Quando desativada, {contextNoun} ficam sem responsável até alguém atribuir manualmente.
          </div>
        </div>
        <Switch
          checked={config.enabled}
          onChange={(checked) => updateConfig({ enabled: checked })}
          aria-label="Ativar distribuição automática"
          data-testid="distribution-enabled"
        />
      </div>

      {/* Só em canais: o responsável pelo lead vem antes da estratégia.
          Nasce ligada — canal que nunca gravou a opção conta como ligada. */}
      {config.enabled && resourceType === 'channel' && (
        <div
          className="flex items-center justify-between gap-4 py-3 border-t"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <div>
            <div
              className="text-sm font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Priorizar o responsável pelo lead
            </div>
            <div
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Quando o cliente tem um lead em aberto, a conversa vai para o responsável por ele — na
              chegada de uma conversa nova e nas transferências do agente de IA. Se ele não estiver
              vinculado a este canal (ou estiver inativo), vale a estratégia abaixo e o motivo fica
              registrado na conversa.
            </div>
          </div>
          <Switch
            checked={config.preferLeadOwner !== false}
            onChange={(checked) => updateConfig({ preferLeadOwner: checked })}
            aria-label="Priorizar o responsável pelo lead"
            data-testid="distribution-prefer-lead-owner"
          />
        </div>
      )}

      {/* Strategy + per-type config — only when enabled */}
      {config.enabled && (
        <div className="space-y-4 mt-4">
          <div>
            <label
              className="block text-sm font-medium mb-3"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Estratégia
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {STRATEGY_OPTIONS.map((option) => {
                const isSelected = config.type === option.value;
                return (
                  <div
                    key={option.value}
                    className="rounded-lg border transition-all"
                    style={{
                      backgroundColor: isSelected
                        ? 'var(--theme-primary-background)'
                        : 'var(--theme-input-background)',
                      borderColor: isSelected
                        ? 'var(--theme-primary)'
                        : 'var(--theme-input-border)',
                      color: 'var(--theme-card-text)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleStrategyChange(option.value)}
                      className="flex flex-col items-start gap-1 p-4 w-full text-left"
                    >
                      <div className="font-medium text-sm">{option.label}</div>
                      <div
                        className="text-xs"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {option.description}
                      </div>
                    </button>

                    {/* Select embutido no card quando selecionado */}
                    {isSelected && option.value === 'fixed_operator' && (
                      <div className="px-4 pb-4">
                        <Select
                          value={config.fixedOperatorConfig?.userId ?? ''}
                          onChange={(value) =>
                            updateConfig({ fixedOperatorConfig: { userId: value } })
                          }
                          options={usersData.map((u) => ({
                            value: u.id,
                            label: u.name ?? u.email,
                          }))}
                          placeholder="Selecione um operador"
                        />
                      </div>
                    )}
                    {isSelected && option.value === 'fixed_team' && (
                      <div className="px-4 pb-4">
                        <Select
                          value={config.fixedTeamConfig?.teamId ?? ''}
                          onChange={(value) =>
                            updateConfig({ fixedTeamConfig: { teamId: value } })
                          }
                          options={(teamsData ?? []).map((t) => ({
                            value: t.id,
                            label: t.name,
                          }))}
                          placeholder="Selecione uma equipe"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentConfigSection;
